import { formatBadgeCount } from '@/lib/format-badge-count';
import type { NotificationTab } from '@fluenceur/types';
import { NotificationTab as NotificationTabValue } from '@fluenceur/types';
import { Box, Tabs, Text } from '@chakra-ui/react';
import { SparkSolid } from 'iconoir-react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

type NotificationTabsProps = {
  agentsCount: number;
  onTabChange: (event: { value: string | null }) => void;
  tab: NotificationTab;
  totalCount: number;
};

/** Selected state is driven by Chakra's `_selected`, not by a computed flag. */
const TRIGGER_STYLE = {
  h: '36px',
  px: '14px',
  gap: '6px',
  rounded: 'full',
  cursor: 'pointer',
  fontFamily: 'heading',
  fontSize: '13.5px',
  fontWeight: 'semibold',
  transition: 'background 0.15s, color 0.15s',
  bg: 'softCream',
  color: 'ink',
  _hover: { bg: 'primary.100' },
  _selected: {
    bg: 'primary.900',
    color: 'primary.contrast',
    // Nested so `&[data-selected]:hover` outranks the base `&:hover` above,
    // which would otherwise lighten the dark selected pill.
    _hover: { bg: 'primary.700' },
  },
  _focusVisible: { outline: '2px solid', outlineColor: 'ink', outlineOffset: '2px' },
};

const TabCount = memo(function TabCount({ active, count }: { active: boolean; count: number }) {
  if (count <= 0) return null;

  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minW="18px"
      h="18px"
      px="5px"
      rounded="full"
      fontSize="11px"
      fontWeight="semibold"
      lineHeight="1"
      bg={active ? 'whiteAlpha.300' : 'panelBg'}
      color={active ? 'primary.contrast' : 'inkMuted'}
    >
      {formatBadgeCount(count)}
    </Box>
  );
});

export const NotificationTabs = memo(function NotificationTabs({
  agentsCount,
  onTabChange,
  tab,
  totalCount,
}: NotificationTabsProps) {
  const { t } = useTranslation();
  const agentsActive = tab === NotificationTabValue.AGENTS;

  return (
    <Tabs.Root value={tab} onValueChange={onTabChange} variant="plain" size="sm">
      {/* The label belongs on the element carrying role="tablist". */}
      <Tabs.List
        aria-label={t('notifications.tabs.label')}
        gap="6px"
        border="none"
        bg="transparent"
        p={0}
      >
        <Tabs.Trigger value={NotificationTabValue.ALL} {...TRIGGER_STYLE}>
          <Text as="span">{t('notifications.tabs.all')}</Text>
          <TabCount count={totalCount} active={!agentsActive} />
        </Tabs.Trigger>

        <Tabs.Trigger value={NotificationTabValue.AGENTS} {...TRIGGER_STYLE}>
          <SparkSolid
            width={13}
            height={13}
            color={agentsActive ? 'currentColor' : 'var(--chakra-colors-lilac-solid)'}
            aria-hidden="true"
          />
          <Text as="span">{t('notifications.tabs.agents')}</Text>
          <TabCount count={agentsCount} active={agentsActive} />
        </Tabs.Trigger>
      </Tabs.List>
    </Tabs.Root>
  );
});
